/* eslint-disable @typescript-eslint/no-misused-promises */
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Box,
  useDisclosure,
  IconButton,
  Icon,
} from '@ouster/webviz-react-components';

import React, { useState } from 'react';
import {
  RiProfileLine,
  RiTeamLine,
  RiFileShield2Line,
  RiBarChartBoxLine,
} from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';

/* 
  comment all password change related below, this feature will be ready in release 3
*/
import HtmlEula from './HtmlEula';
import Profile from './Profile';
import useLogout from '../../hooks/useLogout';
import { useStoreActions, useStoreState } from '../../reducers';
import { useFeedbackForm } from '../../utils/customHooks';
import ModalContainer from '../ModalContainer';
import UserOrgSwitch from './UserOrgSwitch';

const ACTIONS = [
  'PROFILE',
  'ORGANIZATION',
  'EULA',
  'FEEDBACK',
  'SIGN-OUT',
  'ADMIN-DASHBOARD',
] as const;

type TMenuItem = {
  label: string;
  action: typeof ACTIONS[number];
  icon: React.ReactElement;
};

const MENU_ITEMS: TMenuItem[] = [
  {
    label: 'Admin Dashboard',
    action: 'ADMIN-DASHBOARD',
    icon: <RiBarChartBoxLine />,
  },
  { label: 'Manage Profile', action: 'PROFILE', icon: <RiProfileLine /> },
  { label: 'EULA', action: 'EULA', icon: <RiFileShield2Line /> },
  {
    label: 'Organization Settings',
    action: 'ORGANIZATION',
    icon: <RiTeamLine />,
  },
  { label: 'Feedback', action: 'FEEDBACK', icon: <Icon icon="feedback" /> },
  { label: 'Sign Out', action: 'SIGN-OUT', icon: <Icon icon="logout" /> },
];

const MenuHeader = ({ displayName }: { displayName?: string }) => (
  <Box
    fontSize={16}
    fontWeight={500}
    py={2}
    mb={2}
    marginInline={6}
    color="white"
    width="200px"
    whiteSpace="nowrap"
    textOverflow="ellipsis"
    overflow="hidden"
  >
    Hi, {displayName}
  </Box>
);

export default function NavUser() {
  const { appUser, curUserOrg, hasMultiOrgs, userEulaLink, isOusterUser } =
    useStoreState((storeState) => storeState.user);
  const { fetchUserEulaLink } = useStoreActions((actions) => actions.user);

  const {
    isOpen: isDialogOpen,
    onOpen: onDialogOpen,
    onClose: onDialogClose,
  } = useDisclosure();
  const {
    isOpen: isMenuOpen,
    onClose: onMenuClose,
    onOpen: onMenuOpen,
  } = useDisclosure();

  const [selectedMenuItem, setSelectedMenuItem] = useState<TMenuItem | null>(
    null
  );
  const navigate = useNavigate();
  const feedbackFormLink = useFeedbackForm();
  const logout = useLogout();

  const closeUserMenu = () => {
    onMenuClose();
  };

  async function handleMenuClick(menuItem: TMenuItem) {
    switch (menuItem.action) {
      case 'FEEDBACK':
        window.open(feedbackFormLink, '_blank');
        break;
      case 'SIGN-OUT':
        logout();
        break;
      case 'ADMIN-DASHBOARD':
        navigate('/admin_dashboard');
        break;
      case 'ORGANIZATION':
        navigate('/organization');
        break;
      case 'PROFILE':
        setSelectedMenuItem(menuItem);
        onDialogOpen();
        break;
      case 'EULA':
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        await fetchUserEulaLink(appUser!.identity_id);
        setSelectedMenuItem(menuItem);
        onDialogOpen();
        break;
      default:
        console.warn(`Unsupported action: ${menuItem.action}`);
        break;
    }
  }

  const getModalProperties = (selectedItem: TMenuItem | null) => {
    if (!selectedItem) return { header: null, footer: null, content: null };
    const modalConfig = {
      PROFILE: {
        header: 'Profile',
        footer: null,
        content: <Profile handleClose={onDialogClose} />,
      },
      EULA: {
        header: 'View EULA',
        footer: null, // No footer for Profile
        content: (
          <HtmlEula handleClose={onDialogClose} eulaLink={userEulaLink} />
        ),
      },
    };

    const modalProperties =
      modalConfig[selectedItem.action as keyof typeof modalConfig] || {};
    return {
      header: modalProperties.header,
      footer: modalProperties.footer,
      content: modalProperties.content,
    };
  };
  const { header, footer, content } = getModalProperties(selectedMenuItem);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        columnGap={2}
        paddingBlock="6px"
        paddingInline="10px"
        border={1}
        borderColor="gray.600"
        borderStyle="solid"
        borderRadius="8px"
        data-testid="nav-user-root"
      >
        <Box
          as="span"
          display={{ base: 'none', md: 'inline-block' }}
          maxWidth="15ch"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          verticalAlign="bottom"
        >
          {curUserOrg?.name}
        </Box>
        <Menu
          placement="top-end"
          offset={[5, 15]}
          autoSelect={false}
          onClose={onMenuClose}
          isOpen={isMenuOpen}
        >
          <MenuButton
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            as={IconButton}
            icon="manageAccount"
            borderRadius="100%"
            color="white"
            onClick={onMenuOpen}
            data-testid="nav-user-menu-btn"
          />
          <MenuList
            data-testid="nav-user-menu-list"
            rowGap={2}
            maxHeight="80vh"
            overflowY="auto"
          >
            <MenuHeader displayName={appUser?.name || appUser?.email} />
            <MenuDivider borderColor="gray.600" />

            {hasMultiOrgs && (
              <>
                <UserOrgSwitch closeUserMenu={closeUserMenu} />
                <MenuDivider borderColor="gray.600" />
              </>
            )}

            {MENU_ITEMS.map((item) => {
              // Skip rendering "ADMIN-DASHBOARD" if conditions are not met
              if (
                item.action === 'ADMIN-DASHBOARD' &&
                (!isOusterUser || curUserOrg?.role !== 'ADMIN')
              ) {
                return null;
              }
              return (
                <React.Fragment key={item.action}>
                  {' '}
                  {item.action === 'SIGN-OUT' && (
                    <MenuDivider
                      key={`${item.action}-divider`}
                      borderColor="gray.600"
                    />
                  )}
                  <MenuItem
                    key={item.action}
                    onClick={() => handleMenuClick(item)}
                    style={{
                      fontSize: 14,
                      paddingBlock: '0.75rem',
                      paddingInline: '1.25rem',
                      marginBlock: '0.25rem',
                      columnGap: '0.5rem',
                    }}
                    _hover={{ bgColor: 'var(--color-blast-off-blue)' }}
                    _focus={{ bgColor: 'var(--color-blast-off-blue)' }}
                    data-testid={`nav-user-menu-item-${item.action.toLowerCase()}`}
                  >
                    <span style={{ fontSize: 20 }}>{item.icon}</span>
                    {item.label}
                  </MenuItem>
                  {item.action === 'ADMIN-DASHBOARD' && (
                    <MenuDivider
                      key={`${item.action}-divider`}
                      borderColor="gray.600"
                    />
                  )}
                </React.Fragment>
              );
            })}
          </MenuList>
        </Menu>
      </Box>

      <ModalContainer
        isOpen={isDialogOpen}
        onClose={onDialogClose}
        header={header}
        footer={footer}
        content={content}
      />
    </>
  );
}
